import { useCallback, useMemo, useState, type FormEventHandler } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import TextInput from 'components/Common/Form/Input/TextInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import HelpPane from 'components/Common/HelpPane'
import OtpResendHelp from 'components/Common/Otp/OtpResendHelp'
import type { OtpDeliveryChannel } from 'constants/otpDelivery'
import { PENDING_ACTIONS, type PendingAction } from '../constants'
import { ErrorBanner, PendingButtonContent, renderStrongText, StepBackButton, Title } from './shared'

interface Props {
  user: App.User
  autoFocus: boolean
  error: any
  processing: boolean
  countDown: number
  resendLabel: string
  defaultContact?: App.GeoContact
  onBack: () => void
  onSubmit: FormEventHandler<HTMLFormElement>
  onResend: () => void
  onRequestCall: () => void
  isActionPending: (action: PendingAction) => boolean
}

function LoginOtpStep({
  user,
  autoFocus,
  error,
  processing,
  countDown,
  resendLabel,
  defaultContact,
  onBack,
  onSubmit,
  onResend,
  onRequestCall,
  isActionPending,
}: Props) {
  const intl = useIntl()
  const [lastChannel, setLastChannel] = useState<OtpDeliveryChannel>('sms')
  const hasServerError = error?.status === 500
  const codeSentMessage = useMemo(() => (
    lastChannel === 'call' ? (
      <FormattedMessage
        id="0_PhQhpT5RqvMekJ6B_3"
        defaultMessage="We’re calling <strong>{maskedPhone}</strong> with a 6-digit code. The code is valid for 10 minutes and after that you will need to request a new one."
        description="Instructions text explaining OTP code is being delivered by voice call during login"
        values={{
          maskedPhone: user.maskedPhone,
          strong: renderStrongText,
        }}
      />
    ) : (
      <FormattedMessage
        id="Xv4jI5itjgVb3oh2M94I"
        defaultMessage="We’ve sent a 6-digit code to <strong>{maskedPhone}</strong>. The code is valid for 10 minutes and after that you will need to request a new one."
        description="Instructions text explaining OTP code was sent during login"
        values={{
          maskedPhone: user.maskedPhone,
          strong: renderStrongText,
        }}
      />
    )
  ), [lastChannel, user.maskedPhone])
  const didntReceiveText = useMemo(() => intl.formatMessage({
    id: '1ck7CUE-sDA_fqo35aZj',
    defaultMessage: 'Didn’t receive the SMS?',
    description: 'Help text asking if user received the SMS during login OTP',
  }), [intl])
  const handleResendSms = useCallback(() => {
    setLastChannel('sms')
    onResend()
  }, [onResend])
  const handleRequestCall = useCallback(() => {
    setLastChannel('call')
    onRequestCall()
  }, [onRequestCall])

  return (
    <>
      <StepBackButton
        label={intl.formatMessage({
          id: '_I-T4-bL3TaYsTZhGL9E',
          defaultMessage: 'Back',
          description: 'Back button label for embedded account access chat widget',
        })}
        onClick={onBack}
      />
      <Title>{intl.formatMessage({
        id: 'zdBkUU0UIw8AkPBTLjEi',
        defaultMessage: 'Verification code',
        description: 'Modal title for login OTP verification step dialog window',
      })}
      </Title>
      <VerticalSpacer as="form" gap={16} onSubmit={onSubmit}>
        <ErrorBanner
          error={error}
          genericMessage={intl.formatMessage({
            id: 'CH6eCE9HJZnQ6Z4lFA7Z',
            defaultMessage: 'Something went wrong. Please try logging in with your password instead.',
            description: 'Error message when embedded login OTP verification fails',
          })}
        />
        <BodyText variant="medium">
          {codeSentMessage}
        </BodyText>
        <TextInput
          type="text"
          required
          id="embedded-login-code"
          name="code"
          label={intl.formatMessage({
            id: 'EWCDzKriSpvW5Y-DMffF',
            defaultMessage: '6-digit code',
            description: 'Form field label for OTP code input field during login',
          })}
          placeholder={intl.formatMessage({
            id: '_8hCtXHgFdckcvz1D--c',
            defaultMessage: 'Enter 6-digit code',
            description: 'Placeholder for OTP code input during login when field is empty',
          })}
          requiredErrorMessage={intl.formatMessage({
            id: 'ydj4xCsGbzQLoX0Od3-8',
            defaultMessage: 'Code is required',
            description: 'Validation error when OTP code field is empty during login',
          })}
          minLength={6}
          maxLength={6}
          pattern="^[0-9]*$"
          invalidErrorMessage={intl.formatMessage({
            id: '93GllCprPh8ea081t75f',
            defaultMessage: 'Your code must be 6 digits long',
            description: 'Validation error when OTP code is not 6 digits during login',
          })}
          inputMode="numeric"
          autoFocus={autoFocus}
        />
        {!hasServerError && (
          <TextButton kind="primary" shape="round" size="medium" fit="flex" type="submit" disabled={processing}>
            <PendingButtonContent
              pending={isActionPending(PENDING_ACTIONS.submitLoginOtp)}
              label={intl.formatMessage({
                id: 'nC8FmMNMRNbqFBBwdBTv',
                defaultMessage: 'Submit',
                description: 'Submit button text for OTP verification during login',
              })}
            />
          </TextButton>
        )}
      </VerticalSpacer>
      {!hasServerError && (
        <OtpResendHelp
          countDown={countDown}
          prompt={didntReceiveText}
          resendLabel={resendLabel}
          onResendSms={handleResendSms}
          onRequestCall={handleRequestCall}
        />
      )}
      {hasServerError && defaultContact && <HelpPane contactNumber={defaultContact} />}
    </>
  )
}

export default LoginOtpStep
